'use client';

import { useIsMobile } from '@/hooks/use-mobile';

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useEffectEvent,
	useMemo,
	useRef,
	useState,
} from 'react';

import { toast } from '@/shared/components';
import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { generateVideoPresignedUrl } from '@/shared/services/forms/forms';
import { generateRandomSlug } from '@/shared/utils/file-upload';
import {
	checkMediaPermissions,
	getMediaStream,
	getSupportedVideoMimeType,
	stopMediaStream,
} from '@/shared/utils/media-utils';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';

import {
	DEFAULT_VIDEO_CONSTRAINTS,
	MAX_RECORDING_TIME,
	MOBILE_VIDEO_CONSTRAINTS,
} from './constants';
import type {
	ModalState,
	RecordingState,
	SelectedDevices,
	UIState,
	UploadState,
	VideoRecordingContextValue,
	VideoRecordingFieldProps,
	VideoState,
} from './types';
import { type UnmirroredStreamResult, createUnmirroredStream, uploadVideoToS3 } from './utils';

const VideoRecordingContext = createContext<VideoRecordingContextValue | null>(null);

type VideoRecordingProviderProps = {
	children: React.ReactNode;
} & Omit<VideoRecordingFieldProps, 'editMode' | 'disabled' | 'viewOnlyMode'>;

// Initial state constants for DRY resets
const INITIAL_RECORDING_STATE: RecordingState = {
	isRecording: false,
	recordingTime: 0,
	mediaRecorder: null,
	recordedChunks: [],
	stream: null,
};

const INITIAL_VIDEO_STATE: VideoState = {
	recordedVideoUrl: null,
	savedVideoBlob: null,
	savedVideoUrl: null,
	savedVideoDuration: 0,
};

const INITIAL_UI_STATE: UIState = {
	state: 'initial',
	cameraReady: false,
	showVideo: false,
	retakeMode: false,
	error: null,
	showTimeLimitInfo: false,
	isCameraOpen: false,
	isPortrait: false,
};

const INITIAL_MODAL_STATE: ModalState = {
	showConfirmModal: false,
	confirmAction: null,
};

export function VideoRecordingProvider({
	children,
	fieldId,
	onVideoRecorded,
	onVideoRemoved,
	recordedVideo,
	formId,
	formName,
	getVideoPresignedUrl,
	onUploadStateChange,
	onEditModeChange,
	onUnsavedRecordingChange,
	onRecordingStart,
	onRecordingEnd,
	isRecordingBlocked = false,
}: VideoRecordingProviderProps) {
	// Refs
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const videoContainerRef = useRef<HTMLDivElement>(null);
	const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const unmirroredStreamRef = useRef<UnmirroredStreamResult | null>(null);

	// State
	const [recordingState, setRecordingState] = useState<RecordingState>(INITIAL_RECORDING_STATE);
	const [videoState, setVideoState] = useState<VideoState>(INITIAL_VIDEO_STATE);
	const [uiState, setUIState] = useState<UIState>(INITIAL_UI_STATE);
	const [modalState, setModalState] = useState<ModalState>(INITIAL_MODAL_STATE);
	const [uploadState, setUploadState] = useState<UploadState>({ progress: 0 });
	const [selectedDevices, setSelectedDevices] = useState<SelectedDevices>({});

	const isMobile = useIsMobile();

	// Memoized tracking data to avoid repetition
	const trackingData = useMemo(
		() => ({
			feature: MIXPANEL_APP_FEATURES.FORM,
			form_id: formId,
			form_name: formName,
			field_id: fieldId,
		}),
		[formId, formName, fieldId]
	);

	// Helper to emit tracking events
	const track = useCallback(
		(event: string, extraData?: Record<string, unknown>) => {
			emitMixpanelEvent(event, { ...trackingData, ...extraData });
		},
		[trackingData]
	);

	// Helper to set error
	const setError = useCallback((error: string | null) => {
		setUIState((prev) => ({ ...prev, error }));
	}, []);

	// Helper to reset video element
	const resetVideoElement = useCallback((options?: { keepSrc?: boolean }) => {
		if (videoRef.current) {
			if (!options?.keepSrc) {
				videoRef.current.src = '';
			}
			videoRef.current.srcObject = null;
			videoRef.current.controls = false;
			videoRef.current.muted = true;
		}
	}, []);

	// Core upload logic - extracted to avoid duplication
	const performUpload = useCallback(
		async (videoBlob: Blob, duration: number): Promise<void> => {
			if (!formId && !getVideoPresignedUrl) {
				// No formId - just save locally
				setTimeout(() => {
					setUploadState({ progress: 100 });
					setUIState((prev) => ({ ...prev, state: 'saved' }));
					onVideoRecorded?.(videoBlob, undefined, duration);
				}, 1500);
				return;
			}

			onUploadStateChange?.(true);
			setUIState((prev) => ({ ...prev, state: 'uploading' }));
			setUploadState({ progress: 0 });

			track(mixpanelEvents.ACTION_VIDEO_SAVING_STARTED);

			const filename = `${generateRandomSlug(fieldId)}.webm`;
			let presignedUrl: string;
			if (getVideoPresignedUrl) {
				presignedUrl = await getVideoPresignedUrl(fieldId, filename);
			} else if (formId) {
				presignedUrl = (
					await generateVideoPresignedUrl({
						form_id: formId,
						field_id: fieldId,
						name: filename,
					})
				).presignedUrl;
			} else {
				return;
			}

			const videoPath = await uploadVideoToS3(
				videoBlob,
				presignedUrl,
				(progress) => setUploadState({ progress })
			);

			const fileName = videoPath.split('/').pop() || 'video.webm';
			track(mixpanelEvents.COMPLETED_VIDEO_SAVING);

			setTimeout(() => {
				setUploadState({ progress: 100 });
				setUIState((prev) => ({ ...prev, state: 'saved' }));
				onEditModeChange?.(false);
				onVideoRecorded?.(videoBlob, fileName, duration);
				// Unblock submit only once the form has the video path
				onUploadStateChange?.(false);
			}, 1500);
		},
		[
			formId,
			fieldId,
			getVideoPresignedUrl,
			track,
			onUploadStateChange,
			onEditModeChange,
			onVideoRecorded,
		]
	);

	// Handle upload error - extracted to avoid duplication
	const handleUploadError = useCallback(
		(error: unknown, showToast = true) => {
			console.error('Error in saveVideo:', error);
			onUploadStateChange?.(false);
			setUIState((prev) => ({ ...prev, state: 'initial' }));
			track(mixpanelEvents.ACTION_VIDEO_SAVING_FAILED, {
				error_message: error instanceof Error ? error.message : 'Unknown error',
			});
			if (showToast) {
				toast({ title: 'Failed to upload video. Please try again.', variant: 'destructive' });
			}
			setError('Failed to upload video. Please try again.');
		},
		[track, onUploadStateChange, setError]
	);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			// Stop the unmirrored stream's animation loop
			unmirroredStreamRef.current?.stop();
			unmirroredStreamRef.current = null;

			stopMediaStream(recordingState.stream);
			if (videoState.recordedVideoUrl) URL.revokeObjectURL(videoState.recordedVideoUrl);
			if (videoState.savedVideoUrl) URL.revokeObjectURL(videoState.savedVideoUrl);
		};
	}, [recordingState.stream, videoState.recordedVideoUrl, videoState.savedVideoUrl]);

	// Scroll into view when camera opens
	useEffect(() => {
		if (uiState.isCameraOpen && videoContainerRef.current) {
			videoContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
			videoContainerRef.current.classList.add('animate-fade-slide-in');
			setTimeout(() => videoContainerRef.current?.classList.remove('animate-fade-slide-in'), 700);
		}
	}, [uiState.isCameraOpen]);

	// 'preview' is the only state where a recording exists but isn't saved yet
	const hasUnsavedRecording = uiState.state === 'preview';
	const notifyUnsavedRecordingChange = useEffectEvent((hasUnsaved: boolean) =>
		onUnsavedRecordingChange?.(hasUnsaved)
	);
	useEffect(() => {
		notifyUnsavedRecordingChange(hasUnsavedRecording);
	}, [hasUnsavedRecording]);

	// Reset state when parent has no video data
	useEffect(() => {
		if (!recordedVideo) {
			setRecordingState(INITIAL_RECORDING_STATE);
			setVideoState(INITIAL_VIDEO_STATE);
			setUIState(INITIAL_UI_STATE);
			setModalState(INITIAL_MODAL_STATE);
			resetVideoElement();
		}
	}, [recordedVideo, resetVideoElement]);

	// Open camera stream
	const openCameraStream = useCallback(
		async (devices?: SelectedDevices) => {
			setError(null);
			setUIState((prev) => ({ ...prev, cameraReady: false }));

			const permissionStatus = await checkMediaPermissions(['camera', 'microphone']);
			if (permissionStatus === 'denied') {
				track(mixpanelEvents.ACTION_VIDEO_PERMISSION_PREVIOUSLY_DENIED, {
					denial_type: 'browser_settings',
				});
				setError(COPY.fields.videoPermissionBlocked);
				setUIState((prev) => ({
					...prev,
					cameraReady: false,
					isCameraOpen: false,
					showVideo: false,
					retakeMode: false,
					state: 'error',
				}));
				onVideoRemoved?.();
				return;
			}

			// Use lower resolution on mobile for better CPU performance
			// Devices are `ideal`, not `exact`: if a picked device is gone the browser falls back to another
			const baseVideoConstraints = isMobile ? MOBILE_VIDEO_CONSTRAINTS : DEFAULT_VIDEO_CONSTRAINTS;
			const videoConstraints = devices?.videoId
				? { ...baseVideoConstraints, deviceId: { ideal: devices.videoId } }
				: baseVideoConstraints;
			const audioConstraints = devices?.audioId ? { deviceId: { ideal: devices.audioId } } : true;
			const result = await getMediaStream({ video: videoConstraints, audio: audioConstraints });
			if ('error' in result) {
				if (result.error.type === 'NotAllowedError') {
					track(mixpanelEvents.ACTION_VIDEO_PERMISSION_DENIED, { denial_type: 'user_prompt' });
				}
				setError(result.error.message);
				setUIState((prev) => ({
					...prev,
					cameraReady: false,
					isCameraOpen: false,
					showVideo: false,
					retakeMode: false,
					state: 'error',
				}));
				onVideoRemoved?.();
				console.error('Error opening camera:', result.error);
				return;
			}

			setRecordingState((prev) => ({ ...prev, stream: result.stream }));
			// Read back what the browser actually opened so the device pickers show the real device
			setSelectedDevices({
				audioId: result.stream.getAudioTracks()[0]?.getSettings().deviceId,
				videoId: result.stream.getVideoTracks()[0]?.getSettings().deviceId,
			});
			if (videoRef.current) {
				const video = videoRef.current;
				video.srcObject = result.stream;
				video.src = '';
				video.muted = true;
				video.controls = false;

				// Detect orientation once video metadata is loaded
				const handleMetadata = () => {
					const isPortrait = video.videoHeight > video.videoWidth;
					setUIState((prev) => ({ ...prev, cameraReady: true, isPortrait }));
				};

				// Check if metadata is already available
				if (video.videoWidth && video.videoHeight) {
					handleMetadata();
				} else {
					video.addEventListener('loadedmetadata', handleMetadata, { once: true });
				}

				video.play().catch(console.error);
			} else {
				setUIState((prev) => ({ ...prev, cameraReady: true }));
			}
		},
		[track, onVideoRemoved, setError, isMobile]
	);

	// Open camera
	const openCamera = useCallback(
		async (source: 'initial' | 'try_again' = 'initial') => {
			if (isRecordingBlocked) return;

			track(
				source === 'initial'
					? mixpanelEvents.CLICKED_VIDEO_START_RECORDING
					: mixpanelEvents.CLICKED_VIDEO_TRY_AGAIN
			);
			setUIState((prev) => ({
				...prev,
				state: 'camera',
				showVideo: true,
				isCameraOpen: true,
				retakeMode: false,
			}));
			await openCameraStream(selectedDevices);
		},
		[isRecordingBlocked, openCameraStream, track, selectedDevices]
	);

	// Close camera
	const closeCamera = useCallback(() => {
		const { mediaRecorder, isRecording, stream } = recordingState;

		track(mixpanelEvents.ACTION_VIDEO_CAMERA_CLOSED, {
			while_recording: isRecording,
			recording_time: recordingState.recordingTime,
		});

		if (mediaRecorder && isRecording && mediaRecorder.state === 'recording') {
			mediaRecorder.stop();
		}
		if (recordingIntervalRef.current) {
			clearInterval(recordingIntervalRef.current);
			recordingIntervalRef.current = null;
		}

		// Stop the unmirrored stream's animation loop
		unmirroredStreamRef.current?.stop();
		unmirroredStreamRef.current = null;

		stopMediaStream(stream);

		if (videoRef.current) {
			videoRef.current.srcObject = null;
			videoRef.current.src = '';
		}

		setRecordingState(INITIAL_RECORDING_STATE);
		setUIState((prev) => ({
			...prev,
			state: 'initial',
			showVideo: false,
			isCameraOpen: false,
			cameraReady: false,
			error: null,
		}));
	}, [recordingState, track]);

	// Switch mic or camera before recording starts; keeps the other device as is
	const switchDevice = useCallback(
		async (kind: 'audio' | 'video', deviceId: string) => {
			const key = kind === 'audio' ? 'audioId' : 'videoId';
			if (uiState.state !== 'camera' || selectedDevices[key] === deviceId) return;

			track(mixpanelEvents.ACTION_VIDEO_DEVICE_SWITCHED, { kind });
			stopMediaStream(recordingState.stream);
			await openCameraStream({ ...selectedDevices, [key]: deviceId });
		},
		[uiState.state, recordingState.stream, selectedDevices, openCameraStream, track]
	);

	const trackNoAudioDetected = useCallback(() => {
		track(mixpanelEvents.ACTION_VIDEO_NO_AUDIO_DETECTED, {
			recording_time: recordingState.recordingTime,
		});
	}, [track, recordingState.recordingTime]);

	// Start recording
	const startRecording = useCallback(() => {
		const { stream } = recordingState;
		if (!stream || !videoRef.current || !canvasRef.current) return;

		setError(null);
		setUIState((prev) => ({ ...prev, state: 'recording', retakeMode: false }));

		const unmirroredResult = createUnmirroredStream({
			stream,
			video: videoRef.current,
			canvas: canvasRef.current,
			isMobile,
		});
		if (!unmirroredResult) {
			console.error('Failed to create un-mirrored stream');
			return;
		}

		// Store the result for cleanup
		unmirroredStreamRef.current = unmirroredResult;

		const mimeType = getSupportedVideoMimeType();
		const recorder = new MediaRecorder(unmirroredResult.stream, {
			mimeType: mimeType || undefined,
		});
		const chunks: Blob[] = [];
		let hitTimeLimit = false;

		recorder.ondataavailable = (event) => {
			if (event.data.size > 0) chunks.push(event.data);
		};

		// onstop fires AFTER all data is collected - this is where we transition to preview
		recorder.onstop = () => {
			// Stop the unmirrored stream's animation loop
			unmirroredStreamRef.current?.stop();
			unmirroredStreamRef.current = null;

			// Now safe to stop the camera stream (use stream from closure, not state)
			stopMediaStream(stream);

			const videoBlob = new Blob(chunks, { type: mimeType || 'video/webm' });
			const url = URL.createObjectURL(videoBlob);

			// Batch all state updates together
			setRecordingState((prev) => ({
				...prev,
				recordedChunks: chunks,
				isRecording: false,
				stream: null,
			}));
			setVideoState((prev) => ({ ...prev, recordedVideoUrl: url }));
			setUIState((prev) => ({
				...prev,
				state: 'preview',
				showTimeLimitInfo: hitTimeLimit,
			}));

			// Notify parent that recording has ended
			onRecordingEnd?.();
		};

		setRecordingState((prev) => ({ ...prev, mediaRecorder: recorder, recordingTime: 0 }));
		recorder.start(1000);
		setRecordingState((prev) => ({ ...prev, isRecording: true }));
		track(mixpanelEvents.ACTION_VIDEO_START_RECORDING_SUCCESS);

		// Notify parent that recording has started
		onRecordingStart?.();

		recordingIntervalRef.current = setInterval(() => {
			setRecordingState((prev) => {
				const newTime = prev.recordingTime + 1;
				if (newTime >= MAX_RECORDING_TIME) {
					hitTimeLimit = true;
					if (recorder.state === 'recording') recorder.stop(); // onstop will handle the rest
					if (recordingIntervalRef.current) {
						clearInterval(recordingIntervalRef.current);
						recordingIntervalRef.current = null;
					}
					return { ...prev, recordingTime: newTime };
				}
				return { ...prev, recordingTime: newTime };
			});
		}, 1000);
	}, [recordingState, track, setError, isMobile, onRecordingStart, onRecordingEnd]);

	// Stop recording - just stops the recorder, onstop callback handles the rest
	const stopRecording = useCallback(() => {
		const { mediaRecorder, isRecording } = recordingState;

		if (mediaRecorder && isRecording && mediaRecorder.state === 'recording') {
			mediaRecorder.stop(); // onstop callback will handle stream cleanup and UI transition
			if (recordingIntervalRef.current) {
				clearInterval(recordingIntervalRef.current);
				recordingIntervalRef.current = null;
			}
		}
	}, [recordingState]);

	// Show confirmation modal
	const showConfirmation = useCallback((action: 'delete' | 'retake') => {
		setModalState({ showConfirmModal: true, confirmAction: action });
	}, []);

	// Retake recording
	const retakeRecording = useCallback(async () => {
		track(mixpanelEvents.CLICKED_VIDEO_RETAKE);
		setUIState((prev) => ({ ...prev, state: 'camera' }));
		setRecordingState((prev) => ({ ...prev, recordedChunks: [], recordingTime: 0 }));
		resetVideoElement();

		if (videoState.recordedVideoUrl) {
			URL.revokeObjectURL(videoState.recordedVideoUrl);
			setVideoState((prev) => ({ ...prev, recordedVideoUrl: null }));
		}
		await openCameraStream(selectedDevices);
	}, [openCameraStream, videoState.recordedVideoUrl, track, resetVideoElement, selectedDevices]);

	// Retake with confirmation
	const retakeWithConfirmation = useCallback(() => showConfirmation('retake'), [showConfirmation]);

	// Save video - now uses extracted helpers
	const saveVideo = useCallback(async () => {
		const { recordedChunks, recordingTime } = recordingState;
		const { savedVideoUrl, savedVideoBlob, savedVideoDuration } = videoState;

		if (recordedChunks.length > 0) {
			// New recording
			const videoBlob = new Blob(recordedChunks, { type: 'video/webm' });
			if (savedVideoUrl) URL.revokeObjectURL(savedVideoUrl);

			setVideoState((prev) => ({
				...prev,
				savedVideoBlob: videoBlob,
				savedVideoDuration: recordingTime,
				savedVideoUrl: URL.createObjectURL(videoBlob),
			}));

			try {
				await performUpload(videoBlob, recordingTime);
			} catch (error) {
				handleUploadError(error);
			}
		} else if (uiState.state === 'edit' && savedVideoBlob) {
			// Re-save in edit mode
			try {
				await performUpload(savedVideoBlob, savedVideoDuration);
			} catch (error) {
				handleUploadError(error, false);
			}
		}
	}, [recordingState, videoState, uiState.state, performUpload, handleUploadError]);

	// Edit saved video
	const editSavedVideo = useCallback(() => {
		setUIState((prev) => ({ ...prev, state: 'edit' }));
		onEditModeChange?.(true);
	}, [onEditModeChange]);

	// Handle confirmation
	const handleConfirm = useCallback(() => {
		const { confirmAction } = modalState;

		if (confirmAction === 'delete') {
			track(mixpanelEvents.CLICKED_VIDEO_DELETE, { video_state: uiState.state });
			setUIState((prev) => ({ ...prev, state: 'initial' }));
			resetVideoElement();

			if (videoState.savedVideoUrl) {
				URL.revokeObjectURL(videoState.savedVideoUrl);
			}
			setVideoState((prev) => ({ ...prev, savedVideoUrl: null, savedVideoBlob: null }));
			onVideoRemoved?.();
		} else if (confirmAction === 'retake') {
			retakeRecording();
		}

		setModalState(INITIAL_MODAL_STATE);
	}, [
		modalState,
		videoState.savedVideoUrl,
		uiState.state,
		track,
		onVideoRemoved,
		retakeRecording,
		resetVideoElement,
	]);

	// Cancel confirmation
	const handleCancel = useCallback(() => setModalState(INITIAL_MODAL_STATE), []);

	// Delete saved video
	const deleteSavedVideo = useCallback(() => showConfirmation('delete'), [showConfirmation]);

	const contextValue = useMemo<VideoRecordingContextValue>(
		() => ({
			recordingState,
			videoState,
			uiState,
			modalState,
			uploadState,
			isRecordingBlocked,
			selectedDevices,
			videoRef,
			canvasRef,
			videoContainerRef,
			openCamera,
			closeCamera,
			switchDevice,
			trackNoAudioDetected,
			startRecording,
			stopRecording,
			retakeRecording,
			retakeWithConfirmation,
			saveVideo,
			editSavedVideo,
			deleteSavedVideo,
			showConfirmation,
			handleConfirm,
			handleCancel,
			setUIState,
			setError,
		}),
		[
			recordingState,
			videoState,
			uiState,
			modalState,
			uploadState,
			isRecordingBlocked,
			selectedDevices,
			openCamera,
			closeCamera,
			switchDevice,
			trackNoAudioDetected,
			startRecording,
			stopRecording,
			retakeRecording,
			retakeWithConfirmation,
			saveVideo,
			editSavedVideo,
			deleteSavedVideo,
			showConfirmation,
			handleConfirm,
			handleCancel,
			setError,
		]
	);

	return (
		<VideoRecordingContext.Provider value={contextValue}>{children}</VideoRecordingContext.Provider>
	);
}

export function useVideoRecording(): VideoRecordingContextValue {
	const context = useContext(VideoRecordingContext);
	if (!context) {
		throw new Error('useVideoRecording must be used within VideoRecordingProvider');
	}
	return context;
}
